// Who is signed in. Loaded once at start, refreshed after sign-in/out and account changes.
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import {useQueryClient} from '@tanstack/react-query'
import { matchesPermission, type Me, type PermissionRequirement } from '@briary/shared'
import { api, ApiError } from './api'

interface AuthState {
  me: Me | null
  loading: boolean
  refresh: () => Promise<Me | null>
  setMe: (me: Me | null) => void
  signOut: () => Promise<void>
}

const AuthCtx = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMeState] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  const queries=useQueryClient(),identity=useRef('')
  const setMe=useCallback((next:Me|null)=>{
    const key=next?JSON.stringify(next):''
    if(identity.current!==key){queries.clear();identity.current=key}
    setMeState(next)
  },[queries])

  const refresh = useCallback(async () => {
    try {
      const m = await api.get<Me>('/api/auth/me')
      setMe(m)
      return m
    } catch (e) {
      if (!(e instanceof ApiError) || e.status === 401) setMe(null)
      return null
    } finally {
      setLoading(false)
    }
  }, [setMe])

  const signOut = useCallback(async () => {
    await api.post('/api/auth/logout').catch(() => {})
    setMe(null)
    window.location.assign('/login')
  }, [setMe])

  useEffect(() => {
    // First load: only a 401 means signed out. While the server is restarting (an update, a 502/503 or no
    // connection) keep the loading screen and retry for up to a minute instead of sending people to sign in.
    let stop = false
    const first = async (attempt: number): Promise<void> => {
      try {
        setMe(await api.get<Me>('/api/auth/me'))
      } catch (e) {
        if (!stop && !(e instanceof ApiError && e.status === 401) && attempt < 30) {
          await new Promise((r) => setTimeout(r, 2000))
          if (!stop) return first(attempt + 1)
        }
        setMe(null)
      }
      if (!stop) setLoading(false)
    }
    void first(0)
    const onSignedOut = () => setMe(null)
    window.addEventListener('briary:signed-out', onSignedOut)
    return () => {
      stop = true
      window.removeEventListener('briary:signed-out', onSignedOut)
    }
  }, [setMe])

  return <AuthCtx.Provider value={{ me, loading, refresh, setMe, signOut }}>{children}</AuthCtx.Provider>
}

export function useAuth() {
  const a = useContext(AuthCtx)
  if (!a) throw new Error('useAuth outside AuthProvider')
  return a
}

/** A nav item's or route's `perm` for the owner's pages: only super admins pass (no role can grant it). */
export const SUPERADMIN_ONLY = '@superadmin'

export const can = (me: Me | null, perm: PermissionRequirement) => !!me && (me.isSuperadmin || matchesPermission(me.permissions, typeof perm === 'string' ? (perm === SUPERADMIN_ONLY ? [] : perm) : perm.filter(p => p !== SUPERADMIN_ONLY)))

export const initials = (name: string, email: string) => {
  const parts = (name || email).replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?'
}
